import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { FiMenu, FiX, FiUser, FiLogOut, FiChevronDown, FiArrowRight } from "react-icons/fi";
// import Button from "./shared/Button";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;



const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Styled Components
const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2.5rem;
  background-color: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;

  ${({ $scrolled }) => $scrolled && css`
    padding: 0.8rem 2.5rem;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  `}

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  text-decoration: none;
`;

const Logo = styled.img`
  width: 160px;
  height: auto;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 140px;
  }
`;

// const BetaTag = styled.span`
//   background: linear-gradient(135deg, #5ab9d3, #151c48);
//   color: white;
//   font-size: 0.7rem;
//   font-weight: 600;
//   padding: 0.2rem 0.6rem;
//   border-radius: 12px;
//   text-transform: uppercase;
//   letter-spacing: 1px;
//   animation: ${pulse} 2s infinite;
// `;

const Nav = styled.nav`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    background: white;
    flex-direction: column;
    gap: 1.5rem;
    padding: 2rem;
    box-shadow: -5px 0 30px rgba(0, 0, 0, 0.1);
    transform: ${({ $isOpen }) => ($isOpen ? "translateX(0)" : "translateX(100%)")};
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
    z-index: 1001;
    ${({ $isOpen }) =>
      $isOpen &&
      css`
        animation: ${slideIn} 0.3s ease forwards;
      `}
  }
`;


const NavLink = styled(Link)`
  color: #151c48;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.5rem 0;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, #5ab9d3, #151c48);
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  ${({ $active }) => $active && css`
    font-weight: 600;
    &::after {
      width: 100%;
    }
  `}

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0.5rem 1rem;
    width: 100%;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    margin-top: 1rem;
  }
`;

const ButtonBase = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  gap: 0.5rem;
  white-space: nowrap;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.8rem 1.5rem;
  }
`;

// const HighlightButton = styled(ButtonBase)`
//   background: linear-gradient(135deg, #151c48, #2a3480);
//   color: white;
//   box-shadow: 0 4px 15px rgba(21, 28, 72, 0.3);
//   position: relative;
//   overflow: hidden;

//   &::before {
//     content: '';
//     position: absolute;
//     top: 0;
//     left: -100%;
//     width: 100%;
//     height: 100%;
//     background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
//     transition: all 0.6s ease;
//   }

//   &:hover {
//     box-shadow: 0 6px 20px rgba(21, 28, 72, 0.4);
//     transform: translateY(-2px);

//     &::before {
//       left: 100%;
//     }
//   }

//   &:active {
//     transform: translateY(0);
//   }
// `;

const SecondaryButton = styled(ButtonBase)`
  background: transparent;
  color: #151c48;
  border: 2px solid rgba(21, 28, 72, 0.3);
  
  &:hover {
    border-color: #151c48;
    background: rgba(21, 28, 72, 0.05);
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #151c48;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1002;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(21, 28, 72, 0.05);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CloseButton = styled(MenuButton)`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
`;

const UserDropdown = styled.div`
  position: relative;
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #151c48;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(21, 28, 72, 0.05);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  animation: ${fadeIn} 0.3s ease forwards;
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem 1.2rem;
  color: #151c48;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(21, 28, 72, 0.05);
  }

  svg {
    font-size: 1.1rem;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  animation: ${overlayFadeIn} 0.3s ease forwards;
`;

const Header = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <HeaderContainer $scrolled={scrolled}>
      <LogoContainer to="/">
        <Logo src="/assets/logo.png" alt="Logo" />
        {/* <BetaTag>Beta</BetaTag> */}
      </LogoContainer>

      <MenuButton onClick={toggleMenu} aria-label="Menu">
        {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
      </MenuButton>

      <Overlay $isOpen={isMenuOpen} onClick={toggleMenu} />
      
      <Nav $isOpen={isMenuOpen}>
        <CloseButton onClick={toggleMenu} aria-label="Fechar menu">
          <FiX size={24} />
        </CloseButton>

        <NavLink to="/" $active={location.pathname === "/"}>
          Home
        </NavLink>
        {/* <NavLink 
          to="/relocation-companies" 
          $active={location.pathname === "/relocation-companies"}
        >
          Empresas
        </NavLink> */}
        <NavLink 
          to="/visa-info" 
          $active={location.pathname === "/visa-info"}
        >
          Vistos
        </NavLink>
        {/* <NavLink 
          to="/faq" 
          $active={location.pathname === "/faq"}
        >
          FAQ
        </NavLink> */}

        <ButtonGroup>
          {isLoggedIn ? (
            <UserDropdown ref={dropdownRef}>
              <UserButton onClick={toggleDropdown}>
                <FiUser />
                Minha Conta
                <FiChevronDown />
              </UserButton>
              <DropdownMenu $isOpen={isDropdownOpen}>
                <DropdownItem to="/profile">
                  <FiUser /> Perfil
                </DropdownItem>
                <DropdownItem to="/settings">
                  <FiUser /> Configurações
                </DropdownItem>
                <DropdownItem 
                  to="/" 
                  onClick={() => setIsLoggedIn(false)}
                >
                  <FiLogOut /> Sair
                </DropdownItem>
              </DropdownMenu>
            </UserDropdown>
          ) : (
            <>
              <SecondaryButton to="/login">
                <FiUser /> Entrar
              </SecondaryButton>
              {/* <HighlightButton to="/sign-up">
                Cadastre-se
              </HighlightButton> */}
              <SecondaryButton
                            as={Link}
                            to="/sign-up"
                            primary
                            style={{ margin: '0 auto' }}
                          >
              
                            Cadastre-se <FiArrowRight style={{ transition: 'transform 0.3s ease' }} />
                          </SecondaryButton>
            </>
          )}
        </ButtonGroup>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;